import {AxiosInstance} from 'axios';
import {
    AsteriskApiError,
    AsteriskBackendErrorResponse,
    AsteriskMessage,
    AsteriskMessageEnabledRequest,
    AsteriskMessageField,
    AsteriskMessageRequest,
} from '../model/AsteriskMessage';
import createAxiosInstance, {isAxiosError} from './AxiosInstance';

export const ASTERISK_MESSAGES_ENDPOINT = '/admin/asterisk-messages';
export const TTS_REQUEST_TIMEOUT_MS = 120000;

const BAD_REQUEST_STATUS = 400;
const FORBIDDEN_STATUS = 403;
const NOT_FOUND_STATUS = 404;
const SERVICE_UNAVAILABLE_STATUS = 503;

const FIELD_NAMES: AsteriskMessageField[] = ['name', 'spokenText', 'displayText', 'enabled'];

const STATUS_MESSAGES: Record<number, string> = {
    [BAD_REQUEST_STATUS]: 'Controleer de ingevulde gegevens.',
    [FORBIDDEN_STATUS]: 'Je hebt geen beheerdersrechten voor deze actie.',
    [NOT_FOUND_STATUS]: 'Dit telefoonbericht bestaat niet meer.',
    [SERVICE_UNAVAILABLE_STATUS]: 'De spraak kon niet worden gegenereerd. Probeer het opnieuw.',
};

const parseFieldError = (rawError: string): {field?: AsteriskMessageField; message: string} => {
    const separatorIndex = rawError.indexOf(':');
    if (separatorIndex < 0) {
        return {message: rawError};
    }

    const possibleField = rawError.slice(0, separatorIndex).trim() as AsteriskMessageField;
    const message = rawError.slice(separatorIndex + 1).trim();
    if (!FIELD_NAMES.includes(possibleField)) {
        return {message: rawError};
    }

    return {field: possibleField, message: message || rawError};
};

const parseBackendErrors = (data: unknown) => {
    const fieldErrors: AsteriskApiError['fieldErrors'] = {};
    if (!data || typeof data !== 'object') {
        return {fieldErrors};
    }

    const response = data as AsteriskBackendErrorResponse;
    const errors = Array.isArray(response.errors) ? response.errors.filter(Boolean) : [];
    const generalErrors: string[] = [];
    errors.forEach((rawError) => {
        const parsed = parseFieldError(String(rawError));
        if (parsed.field) {
            fieldErrors[parsed.field] = parsed.message;
            return;
        }
        generalErrors.push(parsed.message);
    });

    const message = typeof response.error === 'string' ? response.error : generalErrors.join(' ');
    return {fieldErrors, message: message || undefined};
};

export const getAsteriskApiError = (error: unknown): AsteriskApiError => {
    if (!error || !isAxiosError(error)) {
        return {message: 'Er is een onverwachte fout opgetreden.', fieldErrors: {}};
    }

    const status = error.response?.status;
    const parsed = parseBackendErrors(error.response?.data);
    const statusMessage = status === undefined ? undefined : STATUS_MESSAGES[status];
    return {
        status,
        fieldErrors: parsed.fieldErrors,
        message: parsed.message ?? statusMessage ?? 'De actie is mislukt. Probeer het opnieuw.',
    };
};

export const createAsteriskMessagesApi = (client: AxiosInstance = createAxiosInstance()) => ({
    getAll: async (): Promise<AsteriskMessage[]> => {
        const response = await client.get<AsteriskMessage[]>(ASTERISK_MESSAGES_ENDPOINT);
        return Array.isArray(response.data) ? response.data : [];
    },
    create: async (request: AsteriskMessageRequest): Promise<void> => {
        await client.post(ASTERISK_MESSAGES_ENDPOINT, request, {timeout: TTS_REQUEST_TIMEOUT_MS});
    },
    update: async (id: number, request: AsteriskMessageRequest): Promise<void> => {
        await client.put(`${ASTERISK_MESSAGES_ENDPOINT}/${id}`, request, {timeout: TTS_REQUEST_TIMEOUT_MS});
    },
    updateEnabled: async (id: number, request: AsteriskMessageEnabledRequest): Promise<void> => {
        await client.patch(`${ASTERISK_MESSAGES_ENDPOINT}/${id}/enabled`, request);
    },
    remove: async (id: number): Promise<void> => {
        await client.delete(`${ASTERISK_MESSAGES_ENDPOINT}/${id}`);
    },
});

export type AsteriskMessagesApi = ReturnType<typeof createAsteriskMessagesApi>;
